import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowLeftIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";

const AlertDialog = ({
  type = "error",
  title = "Alert",
  message = "Something happened.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  open = false,
  onClose = () => {},
  onConfirm = () => {},
}) => {
  const styles = {
    error: {
      icon: <ExclamationTriangleIcon className="size-6 text-red-400" />,
      iconBg: "bg-gray-600",
      buttonBg: "bg-red-400 hover:bg-red-500",
    },
    success: {
      icon: <CheckCircleIcon className="size-6 text-orange-400" />,
      iconBg: "bg-gray-600",
      buttonBg: "bg-orange-400 hover:bg-orange-500",
    },
  };

  const { icon, iconBg, buttonBg } = styles[type] || styles.error;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <DialogBackdrop className="fixed inset-0 bg-gray-600/75 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative transform overflow-hidden rounded-lg bg-gray-700 px-4 pb-4 pt-5 shadow-xl transition-all sm:max-w-lg sm:p-6 border border-gray-600">
            <div className="absolute right-0 top-0 pr-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md text-gray-400 hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <div
                className={`mx-auto flex size-10 shrink-0 items-center justify-center rounded-full ${iconBg} sm:mx-0`}
              >
                {icon}
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className="text-lg font-semibold text-orange-400"
                >
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-200">{message}</p>
                </div>
              </div>
            </div>
            <div className="mt-4 sm:flex sm:flex-row-reverse sm:gap-3">
              <button
                type="button"
                onClick={onConfirm}
                className={`inline-flex w-full justify-center rounded-md ${buttonBg} px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm sm:w-auto transition-colors duration-200`}
              >
                {confirmText}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex w-full justify-center rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm ring-1 ring-gray-500 hover:bg-gray-500 sm:w-auto transition-colors duration-200"
              >
                {cancelText}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

const PieceWorkerLogTab = ({
  project,
  activeStatusTab,
  setActiveStatusTab,
  onViewLog,
}) => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(
    location.state?.activeStatusTab || activeStatusTab || "Pending",
  );

  const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

  const [mode, setMode] = useState("history");
  const [stage, setStage] = useState("workOrder");
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [availableItems, setAvailableItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [payPeriod, setPayPeriod] = useState(null);
  const [sundayDates, setSundayDates] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);

  const [bidAreaFilter, setBidAreaFilter] = useState("");
  const [bidAreas, setBidAreas] = useState([]);

  const [dialog, setDialog] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  useEffect(() => {
    if (setActiveStatusTab) {
      setActiveStatusTab(activeTab);
    }
  }, [activeTab, setActiveStatusTab]);

  useEffect(() => {
    const generateSundays = () => {
      const dates = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dayOfWeek = today.getDay();
      const daysToSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
      const currentSunday = new Date(today);
      if (dayOfWeek !== 0) {
        currentSunday.setDate(today.getDate() + daysToSunday);
      }

      for (let i = 0; i < 12; i++) {
        const sunday = new Date(currentSunday);
        sunday.setDate(currentSunday.getDate() - i * 7);
        dates.push({
          value: sunday.toISOString().split("T")[0],
          label: sunday.toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
        });
      }

      setSundayDates(dates);
      setPayPeriod(dates[0]?.value || null);
    };
    generateSundays();
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");

      console.log(project);
      if (project.version === 2) {
        const response = await axios.get(
          `${API_BASE}/pieceworkerLogs2?project=${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setLogs(response.data.data || []);
      } else {
        const response = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/pieceworkerLogs?project=${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setLogs(response.data);
        setFilteredLogs(
          response.data.filter((log) => log.status === "pending"),
        );
      }
    } catch (err) {
      console.error("Error fetching pieceworker logs:", err);
      setError("Failed to load pieceworker logs.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    const statusMap = {
      Pending: "pending",
      Approved: "approved",
      Rejected: "rejected",
    };
    setFilteredLogs(logs.filter((log) => log.status === statusMap[activeTab]));
  }, [activeTab, logs]);

  useEffect(() => {
    const fetchWorkOrders = async () => {
      if (project.version === 2) {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(
            `${API_BASE}/workorders2/project/${projectId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          const pieceworkOrders = response.data.workOrders.filter(
            (wo) => wo.type === "piecework",
          );
          setWorkOrders(pieceworkOrders);
        } catch (err) {
          console.error("Error fetching work orders:", err);
        }
      } else {
        if (project && project.workOrders) {
          const pieceworkOrders = project.workOrders.filter(
            (wo) => wo.type === "piecework",
          );
          setWorkOrders(pieceworkOrders);
        }
      }
    };
    if (projectId) {
      fetchWorkOrders();
    }
  }, [projectId]);

  const handleSelectWorkOrder = async (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setCreateLoading(true);
    setCreateError(null);

    try {
      const token = localStorage.getItem("token");

      if (project.version === 2) {
        const itemsResponse = await axios.get(
          `${API_BASE}/pieceworkerLogs2/work-order/${workOrder._id}/available-items`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const items = itemsResponse.data.items || [];
        setAvailableItems(items);
        const uniqueBidAreas = [
          ...new Set(items.map((item) => item.bidArea).filter(Boolean)),
        ];
        setBidAreas(uniqueBidAreas);
        setBidAreaFilter("");

        const initialQuantities = {};
        items.forEach((item) => {
          initialQuantities[item._id] = "";
        });
        setQuantities(initialQuantities);
      }

      const employeesResponse = await axios.get(
        `${API_BASE}/users/pieceworkers`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEmployees(employeesResponse.data);

      setStage("employee");
    } catch (err) {
      console.error("Error loading work order data:", err);
      setCreateError("Failed to load work order data.");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSelectEmployee = (employeeId) => {
    const employee = employees.find((emp) => emp._id === employeeId);
    setSelectedEmployee(employee);
    setStage("payPeriod");
  };

  const handlePayPeriodConfirm = () => {
    if (!payPeriod) {
      setCreateError("Please select a pay period.");
      return;
    }
    setStage("lineItems");
  };

  const handleQuantityChange = (itemId, value) => {
    const numValue = parseFloat(value) || 0;
    const item = availableItems.find((li) => li._id === itemId);

    if (item && numValue > item.qtyRemaining) {
      setCreateError(`Quantity cannot exceed ${item.qtyRemaining} ${item.uom}`);
      setQuantities((prev) => ({
        ...prev,
        [itemId]: item.qtyRemaining.toString(),
      }));
    } else {
      setCreateError(null);
      setQuantities((prev) => ({ ...prev, [itemId]: value }));
    }
  };

  const handleSubmit = async () => {
    if (!selectedEmployee) {
      setCreateError("Please select an employee.");
      return;
    }

    const lineItemsToSubmit = availableItems
      .filter((item) => {
        const qty = parseFloat(quantities[item._id] || 0);
        return qty > 0;
      })
      .map((item) => ({
        lineItemId: item._id,
        quantity: parseFloat(quantities[item._id]),
        title: item.title,
        uom: item.uom,
        unitCost: item.unitCost,
        bidArea: item.bidArea || "",
      }));

    if (lineItemsToSubmit.length === 0) {
      setCreateError(
        "At least one line item with a quantity greater than 0 is required.",
      );
      return;
    }

    try {
      setCreateLoading(true);
      const token = localStorage.getItem("token");

      const payload = {
        projectId,
        workOrderId: selectedWorkOrder._id,
        employeeId: selectedEmployee._id,
        lineItems: lineItemsToSubmit,
        workCompletedDate: payPeriod,
      };

      const response = await axios.post(
        `${API_BASE}/pieceworkerLogs2`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setLogs((prev) => [response.data.data, ...prev]);
      setActiveTab("Pending");
      setMode("history");

      setDialog({
        open: true,
        type: "success",
        title: "Worksheet Submitted",
        message: "The pieceworker worksheet has been submitted for approval.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });

      resetForm();
    } catch (err) {
      console.error("Error submitting worksheet:", err);
      const errorMsg =
        err.response?.data?.message || "Failed to submit worksheet.";
      setCreateError(errorMsg);
      setDialog({
        open: true,
        type: "error",
        title: "Submission Failed",
        message: errorMsg,
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedWorkOrder(null);
    setAvailableItems([]);
    setEmployees([]);
    setSelectedEmployee(null);
    setQuantities({});
    setStage("workOrder");
    setPayPeriod(sundayDates[0]?.value || null);
    setCreateError(null);
    setBidAreaFilter("");
    setBidAreas([]);
  };

  const handleViewLog = (logId) => {
    if (onViewLog) {
      onViewLog(logId);
    } else {
      navigate(`/piecework-logs2/${logId}`, {
        state: { fromProject: true, projectId, activeStatusTab: activeTab },
      });
    }
  };

  const getAvailabilityColor = (estimated, installed, pending) => {
    const totalUsed = (installed || 0) + (pending || 0);
    const available = (estimated || 0) - totalUsed;
    const percentage = estimated > 0 ? (available / estimated) * 100 : 0;

    if (percentage >= 60) {
      return { bg: "bg-green-400", text: "text-green-400", percentage };
    } else if (percentage >= 20) {
      return { bg: "bg-yellow-400", text: "text-yellow-400", percentage };
    } else {
      return { bg: "bg-red-400", text: "text-red-400", percentage };
    }
  };

  const filteredItems = availableItems.filter(
    (item) => !bidAreaFilter || item.bidArea === bidAreaFilter,
  );

  // Calculate totals for line items
  const calculateSubtotal = () => {
    let total = 0;
    availableItems.forEach((item) => {
      const qty = parseFloat(quantities[item._id] || 0);
      total += qty * (item.unitCost || 0);
    });
    return total;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-gray-200 text-lg">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-red-400 text-lg">{error}</div>
      </div>
    );
  }

  const tabs = ["Pending", "Approved", "Rejected"];

  return (
    <div className="bg-gray-700 rounded-lg shadow-md border border-gray-600">
      <div className="mx-auto px-4 py-6">
        {mode === "history" && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white tracking-tight">
                Pieceworker Logs
              </h2>
              <button
                onClick={() => setMode("create")}
                className="inline-flex items-center px-4 py-2 bg-orange-400 text-gray-200 text-sm font-semibold rounded-md hover:bg-orange-500 transition-colors duration-200 shadow-sm"
              >
                <PlusIcon className="size-4 mr-2" />
                Create New Worksheet
              </button>
            </div>

            <div className="border-b border-gray-600 mb-4">
              <nav className="flex space-x-4">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-2 px-4 text-sm font-medium rounded-t-md transition-colors duration-200 ${
                      activeTab === tab
                        ? "bg-gray-600 text-orange-400 border-b-2 border-orange-400"
                        : "text-gray-400 hover:text-gray-200 hover:bg-gray-600"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>

            <div className="overflow-hidden rounded-lg shadow-xl">
              <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Work Order
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Pay Period
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Total Cost
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {filteredLogs.length > 0 ? (
                    filteredLogs.map((log, index) => (
                      <tr
                        key={log._id}
                        onClick={() => handleViewLog(log._id)}
                        className={`${
                          index % 2 === 0 ? "bg-gray-700" : "bg-gray-600"
                        } hover:bg-gray-500 cursor-pointer transition-colors duration-200`}
                      >
                        <td className="px-4 py-2 text-sm text-gray-200">
                          <div>{log.workOrder?.title || "N/A"}</div>
                          <div className="text-xs text-gray-400">
                            {log.woNumber}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-200">
                          {log.user?.displayName || log.user?.email || "N/A"}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-200">
                          {log.workCompletedDate
                            ? new Date(
                                log.workCompletedDate,
                              ).toLocaleDateString()
                            : "N/A"}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-200">
                          $
                          {(log.totalCost || 0).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                        <td className="px-4 py-2 text-sm text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewLog(log._id);
                            }}
                            className="text-orange-400 hover:text-orange-300 transition-colors duration-200"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-4 py-4 text-center text-sm text-gray-400"
                      >
                        {logs.length === 0
                          ? "No pieceworker logs available."
                          : `No ${activeTab.toLowerCase()} logs found.`}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {mode === "create" && (
          <div className="bg-gray-700 rounded-lg">
            {createError && (
              <div className="bg-red-400/10 border border-red-400 text-red-400 px-4 py-3 rounded-md mb-4">
                {createError}
              </div>
            )}

            {createLoading && (
              <div className="text-center py-4 text-gray-200">Loading...</div>
            )}

            {stage === "workOrder" && !createLoading && (
              <>
                <h2 className="text-lg font-semibold text-orange-400 mb-4">
                  Select Work Order
                </h2>
                {workOrders.length > 0 ? (
                  <div className="grid gap-3">
                    {workOrders.map((wo) => (
                      <div
                        key={wo._id}
                        onClick={() => handleSelectWorkOrder(wo)}
                        className="flex items-center bg-gray-600 p-4 rounded-md cursor-pointer hover:bg-gray-500 transition-colors duration-200"
                      >
                        <div className="w-10 h-10 bg-gray-500 rounded-full flex items-center justify-center mr-3">
                          <svg
                            className="w-5 h-5 text-orange-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                          </svg>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-200">
                            {wo.title}
                          </div>
                          <div className="text-xs text-gray-400">
                            {wo.woNumber}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">
                    No piecework work orders available for this project.
                  </p>
                )}
                <button
                  onClick={() => setMode("history")}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200"
                >
                  <ArrowLeftIcon className="size-4 mr-2" />
                  Back to Logs
                </button>
              </>
            )}

            {stage === "employee" && !createLoading && (
              <>
                <h2 className="text-lg font-semibold text-orange-400 mb-4">
                  Select Pieceworker
                </h2>
                <select
                  value={selectedEmployee?._id || ""}
                  onChange={(e) => handleSelectEmployee(e.target.value)}
                  className="block w-full max-w-md text-sm text-gray-200 bg-gray-600 border border-gray-500 rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="" disabled>
                    Select an employee
                  </option>
                  {employees.map((employee) => (
                    <option key={employee._id} value={employee._id}>
                      {employee.displayName || employee.email}
                    </option>
                  ))}
                </select>
                <div className="flex space-x-4 mt-4">
                  <button
                    onClick={() => setStage("workOrder")}
                    className="inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200"
                  >
                    <ArrowLeftIcon className="size-4 mr-2" />
                    Back
                  </button>
                </div>
              </>
            )}

            {stage === "payPeriod" && !createLoading && (
              <>
                <h2 className="text-lg font-semibold text-orange-400 mb-4">
                  Select Pay Period for{" "}
                  {selectedEmployee?.displayName || selectedEmployee?.email}
                </h2>
                <div className="relative mb-4 max-w-md">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full bg-gray-600 border border-gray-500 rounded-md p-2 text-left text-sm text-gray-200 focus:ring-orange-500 focus:border-orange-500"
                  >
                    {sundayDates.find((d) => d.value === payPeriod)?.label ||
                      "Select a pay period"}
                    <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">
                      {isDropdownOpen ? "▲" : "▼"}
                    </span>
                  </button>
                  {isDropdownOpen && (
                    <div className="absolute w-full bg-gray-700 border border-gray-600 rounded-md mt-1 max-h-40 overflow-y-auto shadow-lg z-10">
                      {sundayDates.map((date) => (
                        <div
                          key={date.value}
                          onClick={() => {
                            setPayPeriod(date.value);
                            setIsDropdownOpen(false);
                          }}
                          className="p-2 hover:bg-gray-600 cursor-pointer text-sm text-gray-200"
                        >
                          {date.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex space-x-4">
                  <button
                    onClick={handlePayPeriodConfirm}
                    className="inline-flex items-center px-4 py-2 bg-orange-400 text-gray-200 text-sm font-semibold rounded-md hover:bg-orange-500 transition-colors duration-200"
                  >
                    Confirm Pay Period
                  </button>
                  <button
                    onClick={() => setStage("employee")}
                    className="inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200"
                  >
                    <ArrowLeftIcon className="size-4 mr-2" />
                    Back
                  </button>
                </div>
              </>
            )}

            {stage === "lineItems" && !createLoading && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-white">
                    Report Quantities for{" "}
                    {selectedEmployee?.displayName || selectedEmployee?.email}
                  </h2>
                  <div>
                    <select
                      value={bidAreaFilter}
                      onChange={(e) => setBidAreaFilter(e.target.value)}
                      className="block text-sm text-gray-200 bg-gray-600 border border-gray-500 rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">All Bid Areas</option>
                      {bidAreas.map((bidArea) => (
                        <option key={bidArea} value={bidArea}>
                          {bidArea}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="text-sm text-gray-400 mb-4">
                  Pay Period Ending:{" "}
                  {sundayDates.find((d) => d.value === payPeriod)?.label}
                </div>

                {filteredItems.length > 0 ? (
                  <div className="space-y-4">
                    {filteredItems.map((item) => {
                      const { bg, text, percentage } = getAvailabilityColor(
                        item.qtyEstimated,
                        item.qtyInstalled || item.qtyApproved,
                        item.qtyPending,
                      );

                      return (
                        <div
                          key={item._id}
                          className="bg-gray-600 p-4 rounded-md"
                        >
                          <h3 className="text-gray-200 font-medium mb-2 text-sm">
                            {item.title}
                          </h3>
                          {item.bidArea && (
                            <p className="text-sm text-gray-400 mb-2">
                              Bid Area: {item.bidArea}
                            </p>
                          )}

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2 text-sm">
                            <p className="text-gray-200">
                              Budget:{" "}
                              {(item.qtyEstimated || 0).toLocaleString()}{" "}
                              {item.uom}
                            </p>
                            <p className="text-gray-200">
                              Installed:{" "}
                              {(
                                item.qtyInstalled ||
                                item.qtyApproved ||
                                0
                              ).toLocaleString()}{" "}
                              {item.uom}
                            </p>
                            <p className="text-gray-200">
                              Pending: {(item.qtyPending || 0).toLocaleString()}{" "}
                              {item.uom}
                            </p>
                            <p className={text}>
                              Available: {item.qtyRemaining.toLocaleString()}{" "}
                              {item.uom} ({percentage.toFixed(1)}%)
                            </p>
                          </div>

                          <div className="w-full bg-gray-500 rounded-full h-1.5 mb-3">
                            <div
                              className={`${bg} h-1.5 rounded-full`}
                              style={{ width: `${Math.min(percentage, 100)}%` }}
                            />
                          </div>

                          <div className="flex items-center mb-2">
                            <input
                              type="number"
                              value={quantities[item._id] || ""}
                              onChange={(e) =>
                                handleQuantityChange(item._id, e.target.value)
                              }
                              placeholder="Qty"
                              min="0"
                              max={item.qtyRemaining}
                              className="bg-gray-700 border border-gray-500 rounded-md p-2 w-24 text-sm text-gray-200 focus:ring-orange-500 focus:border-orange-500"
                            />
                            <span className="ml-2 text-sm text-gray-400">
                              {item.uom}
                            </span>
                            <span className="ml-4 text-sm text-gray-400">
                              × ${item.unitCost?.toFixed(2)} = $
                              {(
                                (quantities[item._id] || 0) * item.unitCost
                              ).toFixed(2)}
                            </span>
                          </div>

                          {item.hasVariance && (
                            <span className="inline-block px-2 py-0.5 text-xs bg-yellow-400/20 text-yellow-400 rounded">
                              VARIANCE
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">
                    No available items to report on.
                  </p>
                )}

                <div className="mt-4 p-4 bg-gray-600 rounded-md">
                  <p className="text-lg font-semibold text-orange-400">
                    Total: $
                    {calculateSubtotal().toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                </div>

                <div className="flex space-x-4 mt-6">
                  <button
                    onClick={handleSubmit}
                    disabled={createLoading}
                    className="inline-flex items-center px-4 py-2 bg-orange-400 text-gray-200 text-sm font-semibold rounded-md hover:bg-orange-500 disabled:bg-gray-600 disabled:opacity-50 transition-colors duration-200"
                  >
                    Submit Worksheet
                  </button>
                  <button
                    onClick={() => setStage("payPeriod")}
                    className="inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200"
                  >
                    <ArrowLeftIcon className="size-4 mr-2" />
                    Back
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <AlertDialog
          open={dialog.open}
          type={dialog.type}
          title={dialog.title}
          message={dialog.message}
          confirmText="OK"
          cancelText="Close"
          onClose={() => setDialog((prev) => ({ ...prev, open: false }))}
          onConfirm={dialog.onConfirm}
        />
      </div>
    </div>
  );
};

export default PieceWorkerLogTab;
